import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  BarChart3,
  Brain,
  Database,
  Zap,
  ArrowRight,
  CheckCircle,
  Upload,
  Cpu,
  LineChart,
  Users,
  Shield,
  Clock,
  Menu,
  X,
} from "lucide-react";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/seo/SEO";
import { useState } from "react";

const Welcome = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    {
      icon: BarChart3,
      title: "Advanced Analytics",
      description:
        "Interactive dashboards with drill-down capabilities. Visualize KPIs, trends, and outliers in real time.",
    },
    {
      icon: Brain,
      title: "AI-Powered Predictions",
      description:
        "Machine learning models that forecast revenue, churn, and demand — no data science degree required.",
    },
    {
      icon: Database,
      title: "Unified Data Hub",
      description:
        "Connect HubSpot, Zoho, Google Sheets, Excel, Dropbox, and more into a single source of truth.",
    },
    {
      icon: Zap,
      title: "Automated Workflows",
      description:
        "Schedule data refreshes, trigger alerts, and generate reports on autopilot.",
    },
  ];

  const steps = [
    {
      icon: Upload,
      step: "01",
      title: "Connect Your Data",
      description:
        "Link your CRM, spreadsheets, cloud storage, or upload CSVs. We handle the rest.",
    },
    {
      icon: Cpu,
      step: "02",
      title: "AI Cleans & Analyzes",
      description:
        "Our engine deduplicates, enriches, and surfaces patterns you'd never find manually.",
    },
    {
      icon: LineChart,
      step: "03",
      title: "Act on Insights",
      description:
        "Get predictive dashboards, automated alerts, and shareable reports your team can trust.",
    },
  ];

  const stats = [
    { value: "500+", label: "Businesses Trust Us" },
    { value: "10M+", label: "Data Points Processed" },
    { value: "99.9%", label: "Platform Uptime" },
    { value: "3 min", label: "Average Setup Time" },
  ];

  const faqs = [
    {
      question: "Is there a free trial?",
      answer:
        "Yes — every account starts with a 14-day free trial with full access to all features. No credit card required.",
    },
    {
      question: "How secure is my data?",
      answer:
        "Enterprise-grade security with end-to-end encryption, SOC 2 compliance, and role-based access controls. Your data never leaves our secured infrastructure.",
    },
    {
      question: "What integrations do you support?",
      answer:
        "We integrate with HubSpot, Zoho, Salesforce, Google Sheets, Excel Online, Dropbox, OneDrive, and more. Custom webhooks are also available for any data source.",
    },
    {
      question: "Do I need technical skills to use Veylo AI?",
      answer:
        "Not at all. Our natural-language query engine lets you ask questions in plain English. The AI handles data cleaning, modeling, and visualization automatically.",
    },
    {
      question: "Can I collaborate with my team?",
      answer:
        "Absolutely. Invite unlimited team members, assign roles and permissions, share dashboards, and leave comments — all in real time.",
    },
    {
      question: "How long does setup take?",
      answer:
        "Most teams are up and running in under 3 minutes. Connect a data source, and our AI immediately starts analyzing your data.",
    },
  ];

  const scrollTo = (id: string) => {
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Veylo AI — AI-Powered Data Analytics & Predictive Insights"
        description="Connect HubSpot, Zoho, Google Sheets and more. Veylo AI cleans data, runs predictions, and builds dashboards in minutes."
        path="/welcome"
        jsonLd={faqJsonLd}
      />
      {/* ─── Sticky Navbar ─── */}
      <nav className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <button
            onClick={() => scrollTo("hero")}
            className="flex items-center gap-2 font-bold text-lg text-foreground"
          >
            <BarChart3 className="h-6 w-6 text-gradient-start" />
            Veylo AI
          </button>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-muted-foreground">
            <button onClick={() => scrollTo("features")} className="hover:text-foreground transition-colors">
              Features
            </button>
            <button onClick={() => scrollTo("how-it-works")} className="hover:text-foreground transition-colors">
              How It Works
            </button>
            <button onClick={() => scrollTo("faq")} className="hover:text-foreground transition-colors">
              FAQ
            </button>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/auth")}>
              Sign In
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href="mailto:info@veyloai.online">Book a Demo</a>
            </Button>
            <Button
              size="sm"
              className="bg-gradient-to-r from-gradient-start to-gradient-end text-hero-foreground hover:opacity-90"
              onClick={() => navigate("/auth")}
            >
              Get Started Free
            </Button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden text-foreground"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-background px-4 py-4 space-y-3">
            <button onClick={() => scrollTo("features")} className="block w-full text-left text-sm text-muted-foreground hover:text-foreground">
              Features
            </button>
            <button onClick={() => scrollTo("how-it-works")} className="block w-full text-left text-sm text-muted-foreground hover:text-foreground">
              How It Works
            </button>
            <button onClick={() => scrollTo("faq")} className="block w-full text-left text-sm text-muted-foreground hover:text-foreground">
              FAQ
            </button>
            <div className="flex flex-col gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => { setMobileMenuOpen(false); navigate("/auth"); }}>
                Sign In
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href="mailto:info@veyloai.online">Book a Demo</a>
              </Button>
              <Button
                size="sm"
                className="bg-gradient-to-r from-gradient-start to-gradient-end text-hero-foreground"
                onClick={() => { setMobileMenuOpen(false); navigate("/auth"); }}
              >
                Get Started
              </Button>
            </div>
          </div>
        )}
      </nav>

      <main>

      {/* ─── Hero ─── */}
      <section
        id="hero"
        className="relative overflow-hidden bg-gradient-to-br from-gradient-start to-gradient-end"
      >
        {/* Decorative circles */}
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 h-[28rem] w-[28rem] rounded-full bg-white/10 blur-3xl" />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 text-center">
          <Badge className="mb-6 bg-white/20 text-hero-foreground border-white/30 hover:bg-white/30">
            Now with AI-powered predictive modeling
          </Badge>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-hero-foreground leading-tight">
            Turn Raw Data Into
            <br />
            Revenue&nbsp;with&nbsp;AI
          </h1>

          <p className="mt-6 max-w-2xl mx-auto text-lg sm:text-xl text-hero-foreground/80">
            Veylo AI connects your data sources, cleans &amp; enriches automatically, and delivers predictive dashboards your whole team can act on — in minutes, not months.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-secondary hover:bg-white/90 text-base px-8"
              onClick={() => navigate("/auth")}
            >
              Start Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/40 text-hero-foreground hover:bg-white/10 text-base px-8"
              asChild
            >
              <a href="mailto:info@veyloai.online">Book a Demo</a>
            </Button>
          </div>
        </div>
      </section>

      {/* ─── Social Proof Bar ─── */}
      <section className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-4 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((s) => (
            <div key={s.label}>
              <p className="text-3xl sm:text-4xl font-extrabold text-foreground">{s.value}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Features Grid ─── */}
      <section id="features" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="outline" className="mb-3">Features</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              Everything you need to outpace the competition
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              From data ingestion to predictive insights — one platform, zero complexity.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f) => (
              <Card
                key={f.title}
                className="group p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-border"
              >
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-gradient-start/20 to-gradient-end/20">
                  <f.icon className="h-6 w-6 text-gradient-start" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section id="how-it-works" className="py-20 sm:py-28 bg-muted/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="outline" className="mb-3">How It Works</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              From zero to insights in 3 steps
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s) => (
              <div key={s.step} className="relative text-center">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-gradient-start to-gradient-end text-hero-foreground">
                  <s.icon className="h-7 w-7" />
                </div>
                <span className="text-xs font-bold tracking-widest text-gradient-start uppercase">
                  Step {s.step}
                </span>
                <h3 className="mt-2 text-xl font-semibold text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Trust Badges ─── */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 grid sm:grid-cols-3 gap-6 text-center">
          {[
            { icon: Shield, label: "Enterprise-Grade Security", sub: "SOC 2 · E2E Encryption" },
            { icon: Users, label: "Team Collaboration", sub: "Roles · Sharing · Comments" },
            { icon: Clock, label: "24 / 7 Support", sub: "Live chat · <2 hr response" },
          ].map((t) => (
            <div key={t.label} className="flex flex-col items-center gap-2">
              <t.icon className="h-8 w-8 text-gradient-start" />
              <p className="font-semibold text-foreground">{t.label}</p>
              <p className="text-xs text-muted-foreground">{t.sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section id="faq" className="py-20 sm:py-28 bg-muted/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-3">FAQ</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="rounded-lg border bg-card px-4">
                <AccordionTrigger className="text-left text-foreground hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gradient-start to-gradient-end">
        <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-4 py-20 sm:py-28 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-hero-foreground">
            Ready to transform your data?
          </h2>
          <p className="mt-4 text-lg text-hero-foreground/80 max-w-xl mx-auto">
            Join 500+ teams already using Veylo AI to make smarter, faster decisions.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-secondary hover:bg-white/90 text-base px-8"
              onClick={() => navigate("/auth")}
            >
              Start Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/40 text-hero-foreground hover:bg-white/10 text-base px-8"
              asChild
            >
              <a href="mailto:info@veyloai.online">Book a Demo</a>
            </Button>
          </div>
        </div>
      </section>

      </main>

      <Footer />
    </div>
  );
};

export default Welcome;
